import {
  flexRender,
  type Cell,
  type Header,
  type Row,
  type RowData,
  type Table as TableType,
} from "@tanstack/react-table";
import { Box, HStack, styled } from "../../../styled-system/jsx";
import { css, cva } from "../../../styled-system/css";
import { Button } from "../core/basic/Button";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";
import { Fragment, memo, useMemo, type ReactNode } from "react";
import { Checkbox } from "../fields/base/Checkbox";
import { SlArrowDown, SlArrowRight } from "react-icons/sl";
import Skeleton from "react-loading-skeleton";
import { Button as RacButton } from "react-aria-components";
import { useIsInEditableWrapper } from "./EditableTableWrapper";
import { ObjectField } from "@formily/react";

const TableHtml = styled("table", {
  base: { display: "grid", minWidth: "100%", borderCollapse: "collapse", transition: "all 0.2s ease" },
});
const TableHeader = styled("thead", { base: { display: "grid", position: "relative", transition: "all 0.2s ease" } });
const TableHeaderRow = styled("tr", { base: { display: "flex", borderBottom: "1px solid {colors.lightgrey}" } });
const HeaderCellContent = styled(RacButton, {
  base: {
    all: "unset",
    boxSizing: "border-box",
    display: "flex",
    position: "relative",
    flex: "1",
    alignItems: "end",
    width: "100%",
    height: "100%",
    textWrap: "balance",
    overflow: "hidden",
  },
});
const TableBody = styled("tbody", { base: { display: "grid", position: "relative", transition: "all 0.2s ease" } });
const TableRow = styled("tr", {
  base: {
    display: "flex",
    borderBottom: "1px solid {colors.lightgrey}",
    width: "100%",
  },
});

function TableHeaderCell<TData extends RowData, TValue>({
  header,
  table,
}: {
  header: Header<TData, TValue>;
  table: TableType<TData>;
}) {
  const justify = header.column.columnDef.meta?.headerJustify ?? header.column.columnDef.meta?.justify ?? "start";
  return (
    <th
      className={css({
        cursor: "pointer",

        display: "flex",
        position: "relative",
        flex: "0 0 auto",
        paddingY: "small",
        color: "grey",
        fontSize: "small",
        fontWeight: "400",
        textTransform: "uppercase",
        transition: "all 0.2s ease",
        _hover: {
          color: "darkgrey",
        },
      })}
      style={{
        minWidth: `max(calc(var(--header-${header.id}-size) * 1px), ${header.column.columnDef.minSize ?? 30}px)`,
        width: `calc(var(--header-${header.id}-size) * 1px)`,
      }}
    >
      {header.isPlaceholder ? null : (
        <>
          <HeaderCellContent
            onPress={header.column.getToggleSortingHandler()}
            justifyContent={justify}
            textAlign={justify}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </HeaderCellContent>
          <HStack gap="xsmall" paddingLeft={"xsmall"}>
            {header.column.getCanSort() && (
              <span className={css({ alignSelf: "end" })}>
                {
                  {
                    asc: <FaSortUp />,
                    desc: <FaSortDown />,
                    false: <FaSort />,
                  }[header.column.getIsSorted().toString()]
                }
              </span>
            )}
            {header.column.getCanResize() && (
              <div
                onDoubleClick={() => header.column.resetSize()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={cva({
                  base: {
                    cursor: "col-resize",
                    zIndex: 1,
                    alignSelf: "end",
                    borderRadius: "full",
                    width: "8px",
                    height: "100%",
                    touchAction: "none",
                    userSelect: "none",
                    _after: {
                      position: "absolute",
                      right: "4px",
                      bottom: "8px",
                      borderRadius: "full",
                      width: "1px",
                      height: "min(100%, 20px)",
                      backgroundColor: "lightGrey",
                      transition: "all 0.2s ease",
                      content: '""',
                    },
                    _hover: {
                      _after: {
                        width: "4px",
                      },
                    },
                  },
                  variants: {
                    isResizing: {
                      true: {
                        _after: {
                          backgroundColor: "trellisPalette.2",
                        },
                      },
                    },
                  },
                })({ isResizing: header.column.getIsResizing() })}
                style={{
                  transform:
                    table.options.columnResizeMode === "onEnd" && header.column.getIsResizing() ?
                      `translateX(${
                        (table.options.columnResizeDirection === "rtl" ? -1 : 1) *
                        (table.getState().columnSizingInfo.deltaOffset ?? 0)
                      }px)`
                    : "",
                }}
              />
            )}
          </HStack>
        </>
      )}
    </th>
  );
}
function TableCell<TData, TValue>({ cell }: { cell: Cell<TData, TValue> }) {
  const justify = cell.column.columnDef.meta?.justify ?? "start";
  return (
    <td
      className={css({
        display: "flex",
        position: "relative",
        flex: "0 0 auto",
        justifyContent: justify,
        alignItems: "center",
        paddingY: "xsmall",
        paddingRight: "small",
        color: "darkblue",
        fontSize: "medium",
        fontWeight: "400",
        textAlign: justify,
        overflowX: "hidden",
        transition: "all 0.2s ease",
      })}
      style={{
        minWidth: `max(calc(var(--col-${cell.column.id}-size) * 1px), ${cell.column.columnDef.minSize ?? 30}px)`,
        width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}

export function Table<TData extends RowData>({
  table,
  renderExpander,
  className,
  enableSelectAll = true,
}: {
  table: TableType<TData>;
  renderExpander?: (row: Row<TData>) => ReactNode;
  className?: string;
  enableSelectAll?: boolean;
}) {
  const canSelect = table.options.enableRowSelection !== false;
  const canExpand = !!table.options.getExpandedRowModel && !!renderExpander;

  const { columnSizing, columnSizingInfo, columnVisibility } = table.getState();

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
    // From https://tanstack.com/table/latest/docs/framework/react/examples/column-resizing-performant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizing, columnSizingInfo, columnVisibility]);

  return (
    <Box overflowX={"auto"}>
      <TableHtml
        className={className}
        style={{
          ...columnSizeVars,
          width: table.getTotalSize(),
        }}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id}>
              {canSelect && enableSelectAll && table.options.enableMultiRowSelection && (
                <th
                  className={css({
                    display: "flex",
                    position: "relative",
                    flex: "0 0 auto",
                    justifyContent: "center",
                    alignItems: "end",
                    width: "24px",
                    paddingBottom: "12px",
                  })}
                >
                  <Checkbox
                    size={"small"}
                    value={
                      table.getIsAllRowsSelected() ? true
                      : table.getIsSomeRowsSelected() ?
                        "indeterminate"
                      : false
                    }
                    onChange={(value) => (value ? table.toggleAllRowsSelected(value) : table.resetRowSelection())}
                  />
                </th>
              )}
              {canExpand && (
                <th
                  className={css({
                    display: "flex",
                    position: "relative",
                    flex: "0 0 auto",
                    justifyContent: "center",
                    width: "24px",
                  })}
                />
              )}
              {headerGroup.headers.map((header) => (
                <TableHeaderCell key={header.id} header={header} table={table} />
              ))}
            </TableHeaderRow>
          ))}
        </TableHeader>
        {columnSizingInfo.isResizingColumn ?
          <MemoizedTable table={table} canExpand={canExpand} canSelect={canSelect} renderExpander={renderExpander} />
        : <BeforeMemoTable table={table} canExpand={canExpand} canSelect={canSelect} renderExpander={renderExpander} />}
      </TableHtml>
    </Box>
  );
}

// These shenanigans are for resizing performance, see https://tanstack.com/table/latest/docs/framework/react/examples/column-resizing-performant
function BeforeMemoTable<TData extends RowData>({
  table,
  canExpand,
  canSelect,
  renderExpander,
}: {
  table: TableType<TData>;
  canExpand: boolean;
  canSelect: boolean;
  renderExpander: ((row: Row<TData>) => ReactNode) | undefined;
}) {
  const isInEditableWrapper = useIsInEditableWrapper();
  const RowWrapper = (isInEditableWrapper ? ObjectField : Fragment) as React.ComponentType<{ children?: ReactNode }>;

  return (
    <TableBody>
      {table.options.meta?.showSkeleton ?
        Array.from(Array(table.getState().pagination.pageSize), (_, i) => (
          <TableRow key={i}>
            <td
              className={css({
                display: "flex",
                position: "relative",
                flex: "0 0 auto",
                justifyContent: "center",
                width: "100%",
                paddingY: "small",
              })}
              colSpan={table.getVisibleFlatColumns().length + Number(canExpand) + Number(canSelect)}
            >
              <Skeleton containerClassName={css({ flex: 1 })} borderRadius={"9999px"} width={"100%"} height={"24px"} />
            </td>
          </TableRow>
        ))
      : table.getRowModel().rows.map((row) => {
          // Shenanigans due to Fragment being unhappy when passed other props
          const rowWrapperProps = isInEditableWrapper ? { name: row.id } : {};
          return (
            <RowWrapper key={row.id} {...rowWrapperProps}>
              <TableRow>
                {canSelect && (
                  <td
                    className={css({
                      display: "flex",
                      position: "relative",
                      flex: "0 0 auto",
                      justifyContent: "center",
                      width: "24px",
                    })}
                  >
                    <Checkbox
                      size={"small"}
                      className={css({
                        marginX: "auto",
                      })}
                      value={row.getIsSelected()}
                      isDisabled={!row.getCanSelect()}
                      onChange={row.toggleSelected}
                    />
                  </td>
                )}
                {canExpand && (
                  <td
                    className={css({
                      display: "flex",
                      position: "relative",
                      flex: "0 0 auto",
                      justifyContent: "center",
                      width: "24px",
                    })}
                  >
                    <Button
                      variant={"contentOnly"}
                      onPress={row.getToggleExpandedHandler()}
                      isDisabled={!row.getCanExpand()}
                      fontSize={"xlarge"}
                      fontWeight={"initial"}
                    >
                      {row.getIsExpanded() ?
                        <SlArrowDown />
                      : <SlArrowRight />}
                    </Button>
                  </td>
                )}
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} cell={cell} />
                ))}
              </TableRow>
              {row.getIsExpanded() && renderExpander?.(row)}
            </RowWrapper>
          );
        })
      }
    </TableBody>
  );
}

const MemoizedTable = memo(
  BeforeMemoTable,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof BeforeMemoTable;
